import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import Fuse from 'fuse.js'
import {
  fetchCandidates,
  approveCandidate,
  skipCandidate,
  deleteCandidate,
  type Candidate,
} from '../api'
import CandidateCard from './CandidateCard'
import LoadingView from './LoadingView'
import SearchBar from './SearchBar'

interface Props {
  refreshKey?: number
  onApprove?: () => void
}

export default function CandidatesPage({ refreshKey, onApprove }: Props) {
  const [candidates, setCandidates]   = useState<Candidate[]>([])
  const [loading,    setLoading]      = useState(false)
  const [error,      setError]        = useState<string | null>(null)
  const [query,      setQuery]        = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await fetchCandidates()
      list.sort((a, b) => {
        const da = a.date_added ? new Date(a.date_added).getTime() : 0
        const db = b.date_added ? new Date(b.date_added).getTime() : 0
        if (db !== da) return db - da
        return 0
      })
      setCandidates(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load candidates')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Reload when parent signals this page is stale
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return }
    load()
  }, [refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const fuse = useMemo(() => new Fuse(candidates, {
    keys: [
      { name: 'label',       weight: 2 },
      { name: 'genre',       weight: 1 },
      { name: 'location',    weight: 1 },
      { name: 'description', weight: 0.5 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
  }), [candidates])

  const visible = query.trim()
    ? fuse.search(query).map(r => r.item)
    : candidates

  async function handleApprove(itemId: string, label?: string) {
    await approveCandidate(itemId, label)
    onApprove?.()
    setTimeout(load, 600)
  }

  async function handleSkip(itemId: string, label?: string) {
    await skipCandidate(itemId, label)
    setTimeout(load, 600)
  }

  async function handleDelete(itemId: string) {
    await deleteCandidate(itemId)
    setTimeout(load, 600)
  }

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <div
        style={{
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <SearchBar value={query} onChange={setQuery} placeholder="Search candidates…" />

        {/* Count */}
        {loading ? <LoadingView /> : (
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            {query.trim()
              ? `${visible.length} of ${candidates.length} candidate${candidates.length !== 1 ? 's' : ''}`
              : `${candidates.length} candidate${candidates.length !== 1 ? 's' : ''}`}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div style={{
            padding: '10px 14px',
            background: '#450a0a',
            color: '#fca5a5',
            fontSize: '13px',
            borderRadius: '8px',
            border: '1px solid #7f1d1d',
          }}>
            ⚠ {error}
          </div>
        )}

        {/* Candidate cards */}
        {!loading && candidates.length === 0 && !error && (
          <div style={{
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: '14px',
            marginTop: '40px',
            opacity: 0.6,
          }}>
            No candidates yet.
            <br />
            <span style={{ fontSize: '12px' }}>
              Run research to scan co-bill artists from shows.
            </span>
          </div>
        )}

        {visible.map((c) => (
          <CandidateCard
            key={`${c.item_id}-${c.label}`}
            candidate={c}
            onApprove={() => handleApprove(c.item_id, c.label)}
            onSkip={() => handleSkip(c.item_id, c.label)}
            onDelete={() => handleDelete(c.item_id)}
          />
        ))}
      </div>
    </div>
  )
}
